import type { OrgOrdersUrlFilters } from './urlParams';

/**
 * Query params for the orders list endpoint.
 *
 * Multi-selects are comma-joined here even though they are repeated keys in
 * the URL — the two formats are not the same and this is where they diverge.
 * Empty values are dropped rather than sent blank.
 */
export const buildListParams = (
  filters: OrgOrdersUrlFilters & {
    page: number;
    pageSize: number;
    sortBy?: string | null;
    sortDirection?: 'asc' | 'desc';
    /** The debounced, trimmed term — not the raw input. */
    search: string;
  }
) => ({
  status: filters.status.join(',') || undefined,
  search: filters.search || undefined,
  productId: filters.productId.join(',') || undefined,
  locationId: filters.locationId.join(',') || undefined,
  deliveryState: filters.deliveryState.join(',') || undefined,
  pickupState: filters.pickupState.join(',') || undefined,
  pickupCity: filters.pickupCity.join(',') || undefined,
  truckNumber: filters.truckNumber.join(',') || undefined,
  driverName: filters.driverName.join(',') || undefined,
  carrierId: filters.carrierId.join(',') || undefined,
  loadOption: filters.loadOption.join(',') || undefined,
  orderDateFrom: filters.orderDateFrom || undefined,
  orderDateTo: filters.orderDateTo || undefined,
  pickupDateFrom: filters.pickupDateFrom || undefined,
  pickupDateTo: filters.pickupDateTo || undefined,
  deliveryDateFrom: filters.deliveryDateFrom || undefined,
  deliveryDateTo: filters.deliveryDateTo || undefined,
  page: filters.page,
  pageSize: filters.pageSize,
  sortBy: filters.sortBy || undefined,
  sortDir: filters.sortDirection,
  salesRep: filters.salesRep.join(',') || undefined,
  tag: filters.tag.join(',') || undefined,
});

/** One order item is one table row, with the order's fields lifted onto it. */
export const flattenOrderRows = (items: any[]): any[] =>
  (items || []).map((item: any) => ({
    ...item,
    orderId: item.order?.id,
    orderCreatedAt: item.order?.createdAt,
    orderStatus: item.order?.status,
  }));

/** At least one page, even with no results, so the pager never reads "1 / 0". */
export const computeTotalPages = (total: number, pageSize: number): number =>
  Math.max(1, Math.ceil(total / pageSize));
